import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  Modal,
  Multiselect,
  Select,
  Spinner,
  SpaceBetween,
  Table,
  type MultiselectProps,
} from '@cloudscape-design/components';
import {
  PageLayout,
  PageSkeleton,
  EmptyState,
  useAuth,
  useRoles,
  useNotify,
  createApiClient,
} from '@deloitte-us-consulting/gp-sdk';
import {
  buildModelGroups,
  labelFor as modelLabel,
  preferredDefault,
  unavailableModels,
  type LiveModel,
} from './aiConfigModels';
import { formatUsd } from './usageTrend';
import { UsageTrendChart } from './UsageTrendChart';

/** Governance config served by GET/PUT /api/ai/config (admin only). */
interface AiConfig {
  defaultModel: string;
  allowedModels: string[];
  maxTokens: number;
  perUserDailyTokenLimit: number;
  updatedAt?: string;
  updatedBy?: string;
}

/** Usage data served by GET /api/ai/usage/* (admin only). */
interface UsagePoint {
  date: string;
  tokens: number;
  /** Estimated USD cost at published Anthropic rates — not a billing reconciliation. */
  costUsd: number;
}
interface AppUsageRow {
  appId: string;
  displayName: string;
  tokensToday: number;
  costUsdToday: number;
}
/**
 * Row from GET /api/ai/usage/top-users — `id` is the email when the platform
 * could resolve one, otherwise the raw stored key (a Cognito sub).
 */
interface TopUserRow {
  id: string;
  email: string | null;
  name: string | null;
  tokensToday: number;
  costUsdToday: number;
}
interface UsageDetail {
  tokensToday: number;
  costUsdToday: number;
  history: UsagePoint[];
}

/** What the shared trend modal is currently showing. */
interface TrendTarget {
  kind: 'app' | 'user';
  id: string;
  label: string;
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/**
 * Admin dashboard for the AI gateway — switch the model powering the platform
 * assistant and set usage limits, without a redeploy. Gated to platform admins
 * (the Admin role); the API also enforces the same gate server-side.
 */
export default function AiConfigAdmin() {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Ai:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [cfg, setCfg] = useState<AiConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<AiConfig>('/api/ai/config');
        if (!cancelled) setCfg(data);
      } catch (e) {
        if (!cancelled) {
          notify({ type: 'error', header: 'Could not load AI settings', content: (e as Error).message });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once when admin status / api client settle. Intentionally NOT depending
    // on `notify` — it's a fresh function each render, which would re-run this
    // effect on every render and clobber the user's in-progress edits.
  }, [api, isAllowed]);

  // What the gateway's Anthropic key can actually call. null = not loaded or the
  // Models API failed; the picker then falls back to the static catalog.
  const [liveModels, setLiveModels] = useState<LiveModel[] | null>(null);
  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<LiveModel[]>('/api/ai/models');
        if (!cancelled) setLiveModels(data);
      } catch {
        // Best-effort: the API re-validates on save when it can reach Anthropic.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed]);

  // --- Usage section state. Kept separate from `cfg` — different lifecycle (not
  // part of the PUT-able config), and its own failures shouldn't block editing
  // Model policy / Limits above, so each piece has a local loading/error state.
  const [appUsage, setAppUsage] = useState<AppUsageRow[] | null>(null);
  const [appUsageError, setAppUsageError] = useState<string | null>(null);

  const [topUsers, setTopUsers] = useState<TopUserRow[] | null>(null);
  const [topUsersError, setTopUsersError] = useState<string | null>(null);

  const [userEmailInput, setUserEmailInput] = useState('');
  const [userLookup, setUserLookup] = useState<{ email: string; detail: UsageDetail } | null>(null);
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const [userLookupError, setUserLookupError] = useState<string | null>(null);

  const [trendTarget, setTrendTarget] = useState<TrendTarget | null>(null);
  const [trendData, setTrendData] = useState<UsageDetail | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  // Tokens and cost live on very different scales, so the chart shows one
  // series at a time rather than a dual-axis line chart.
  const [trendMetric, setTrendMetric] = useState<'tokens' | 'cost'>('tokens');

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<AppUsageRow[]>('/api/ai/usage/apps');
        // Leaderboard: highest usage first. All registered apps are included
        // (even zero-usage ones) since this list comes from the registry, not
        // just apps that have ever made a request.
        if (!cancelled) setAppUsage([...data].sort((a, b) => b.tokensToday - a.tokensToday));
      } catch (e) {
        if (!cancelled) setAppUsageError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<TopUserRow[]>('/api/ai/usage/top-users?limit=10');
        if (!cancelled) setTopUsers(data);
      } catch (e) {
        if (!cancelled) setTopUsersError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isAllowed]);

  // Shared trend fetch — used by both the per-app "View trend" buttons and the
  // per-user lookup, so there's only one chart/modal implementation.
  useEffect(() => {
    if (!trendTarget) return;
    let cancelled = false;
    setTrendLoading(true);
    setTrendError(null);
    setTrendMetric('tokens');
    (async () => {
      try {
        const path =
          trendTarget.kind === 'app'
            ? `/api/ai/usage/app/${encodeURIComponent(trendTarget.id)}?days=30`
            : `/api/ai/usage/user/${encodeURIComponent(trendTarget.id)}?days=30`;
        const data = await api.get<UsageDetail>(path);
        if (!cancelled) setTrendData(data);
      } catch (e) {
        if (!cancelled) setTrendError((e as Error).message);
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, trendTarget]);

  const lookupUser = useCallback(async () => {
    const email = userEmailInput.trim();
    if (!email) return;
    setUserLookupLoading(true);
    setUserLookupError(null);
    try {
      const detail = await api.get<UsageDetail>(`/api/ai/usage/user/${encodeURIComponent(email)}?days=30`);
      setUserLookup({ email, detail });
    } catch (e) {
      setUserLookup(null);
      setUserLookupError((e as Error).message);
    } finally {
      setUserLookupLoading(false);
    }
  }, [api, userEmailInput]);

  if (!isAllowed) {
    return (
      <PageLayout title="AI settings">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </PageLayout>
    );
  }
  if (loading) {
    return (
      <PageLayout title="AI settings">
        <PageSkeleton label="Loading AI settings…" />
      </PageLayout>
    );
  }
  if (!cfg) {
    return (
      <PageLayout title="AI settings">
        <EmptyState
          title="Couldn't load AI settings"
          description="Check that the platform API is deployed and you hold the Admin role, then reload."
        />
      </PageLayout>
    );
  }

  const modelGroups = buildModelGroups(liveModels).map((g) => ({
    label: g.label,
    options: g.options.map((o) => ({ value: o.value, label: o.label, ...(o.description ? { description: o.description } : {}) })),
  }));
  const unavailable = unavailableModels(cfg.allowedModels, liveModels);

  const update = (patch: Partial<AiConfig>) => setCfg((c) => (c ? { ...c, ...patch } : c));

  const onAllowedChange = (selected: readonly MultiselectProps.Option[]) => {
    const allowed = selected.map((o) => o.value).filter((v): v is string => !!v);
    // Keep the default valid. If it fell out of the allow-list, take the first
    // allowed model in catalog order (recommended first), not selection order —
    // otherwise swapping Sonnet 5 for Sonnet 5.5 silently made Haiku the default.
    const fallback = preferredDefault(allowed);
    update({
      allowedModels: allowed,
      defaultModel: allowed.includes(cfg.defaultModel) ? cfg.defaultModel : (fallback ?? cfg.defaultModel),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.put<AiConfig>('/api/ai/config', {
        defaultModel: cfg.defaultModel,
        allowedModels: cfg.allowedModels,
        maxTokens: cfg.maxTokens,
        perUserDailyTokenLimit: cfg.perUserDailyTokenLimit,
      });
      setCfg(saved);
      notify({ type: 'success', header: 'AI settings saved', content: `Default model is now ${modelLabel(saved.defaultModel, liveModels)}.` });
    } catch (e) {
      notify({ type: 'error', header: 'Save failed', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title="AI settings"
      description="Model policy and usage limits for the platform assistant. Changes apply on the next request — no redeploy."
      actions={
        <Button variant="primary" loading={saving} onClick={save}>
          Save
        </Button>
      }
    >
      <Form>
        <SpaceBetween size="l">
          <Container header={<Header variant="h2">Model policy</Header>}>
            <SpaceBetween size="l">
              {unavailable.length > 0 ? (
                <Alert type="warning" header="Some selected models aren't callable">
                  The gateway's Anthropic key can't call {unavailable.join(', ')}. Requests routed to them would fail, and
                  saving is refused until they're removed.
                </Alert>
              ) : null}
              <FormField
                label="Allowed models"
                description={
                  liveModels
                    ? 'Models apps are permitted to use. Only models the gateway’s Anthropic key can call are listed.'
                    : 'Models apps are permitted to use. (Could not check availability with Anthropic; it is re-checked on save.)'
                }
              >
                <Multiselect
                  selectedOptions={cfg.allowedModels.map((v) => ({ value: v, label: modelLabel(v, liveModels) }))}
                  options={modelGroups}
                  onChange={({ detail }) => onAllowedChange(detail.selectedOptions)}
                  placeholder="Choose models"
                />
              </FormField>
              <FormField label="Default model" description="Used when an app doesn't request a specific allowed model.">
                <Select
                  selectedOption={{ value: cfg.defaultModel, label: modelLabel(cfg.defaultModel, liveModels) }}
                  options={cfg.allowedModels.map((v) => ({ value: v, label: modelLabel(v, liveModels) }))}
                  onChange={({ detail }) => update({ defaultModel: detail.selectedOption.value ?? cfg.defaultModel })}
                />
              </FormField>
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h2">Limits</Header>}>
            <SpaceBetween size="l">
              <FormField label="Max tokens per response" description="256–8192. Keep modest until streaming (Phase 4b) lands.">
                <Input
                  type="number"
                  value={String(cfg.maxTokens)}
                  onChange={({ detail }) => update({ maxTokens: Number(detail.value) })}
                />
              </FormField>
              <FormField label="Per-user daily token limit" description="0 = unlimited.">
                <Input
                  type="number"
                  value={String(cfg.perUserDailyTokenLimit)}
                  onChange={({ detail }) => update({ perUserDailyTokenLimit: Number(detail.value) })}
                />
              </FormField>
              {cfg.updatedAt ? (
                <Box color="text-body-secondary" fontSize="body-s">
                  Last updated {new Date(cfg.updatedAt).toLocaleString()}
                  {cfg.updatedBy ? ` by ${cfg.updatedBy}` : ''}.
                </Box>
              ) : null}
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h2">Usage</Header>}>
            <SpaceBetween size="l">
              <FormField label="By app" description="Today's token usage for every registered app, highest first.">
                {appUsageError ? (
                  <Alert type="error">Could not load app usage: {appUsageError}</Alert>
                ) : appUsage === null ? (
                  <Spinner />
                ) : (
                  <Table
                    items={appUsage}
                    variant="embedded"
                    columnDefinitions={[
                      { header: 'App', cell: (a) => a.displayName || a.appId },
                      { header: 'Tokens today', cell: (a) => a.tokensToday.toLocaleString() },
                      { header: 'Cost today', cell: (a) => formatUsd(a.costUsdToday) },
                      {
                        header: '',
                        cell: (a) => (
                          <Button
                            variant="inline-link"
                            onClick={() => setTrendTarget({ kind: 'app', id: a.appId, label: a.displayName || a.appId })}
                          >
                            View trend
                          </Button>
                        ),
                      },
                    ]}
                    empty={<Box color="text-body-secondary">No registered apps.</Box>}
                  />
                )}
              </FormField>

              <FormField label="By user" description="Today's top users by token usage. There's no directory of every user, so this ranks only people who've used the assistant today.">
                {topUsersError ? (
                  <Alert type="error">Could not load top users: {topUsersError}</Alert>
                ) : topUsers === null ? (
                  <Spinner />
                ) : (
                  <Table
                    items={topUsers}
                    variant="embedded"
                    columnDefinitions={[
                      {
                        header: 'User',
                        cell: (u) =>
                          u.email ? (
                            <SpaceBetween size="xxxs">
                              <Box>{u.email}</Box>
                              {u.name ? (
                                <Box color="text-body-secondary" fontSize="body-s">
                                  {u.name}
                                </Box>
                              ) : null}
                            </SpaceBetween>
                          ) : (
                            <SpaceBetween size="xxxs">
                              <Box color="text-body-secondary">Email unknown</Box>
                              <Box color="text-body-secondary" fontSize="body-s">
                                {u.id}
                              </Box>
                            </SpaceBetween>
                          ),
                      },
                      { header: 'Tokens today', cell: (u) => u.tokensToday.toLocaleString() },
                      { header: 'Cost today', cell: (u) => formatUsd(u.costUsdToday) },
                      {
                        header: '',
                        cell: (u) => (
                          <Button
                            variant="inline-link"
                            onClick={() => setTrendTarget({ kind: 'user', id: u.id, label: u.email ?? u.id })}
                          >
                            View trend
                          </Button>
                        ),
                      },
                    ]}
                    empty={<Box color="text-body-secondary">No usage recorded today.</Box>}
                  />
                )}
              </FormField>

              <FormField
                label="Look up a specific user"
                description="Check someone not in the top list above, by their email address. An unrecognized email shows 0, the same as someone who's simply never used the assistant."
              >
                <SpaceBetween size="s">
                  <SpaceBetween size="xs" direction="horizontal">
                    <Input
                      value={userEmailInput}
                      onChange={({ detail }) => setUserEmailInput(detail.value)}
                      placeholder="e.g. jane.smith@deloitte.com"
                    />
                    <Button loading={userLookupLoading} onClick={lookupUser} disabled={!userEmailInput.trim()}>
                      Look up
                    </Button>
                  </SpaceBetween>
                  {userLookupError ? <Alert type="error">{userLookupError}</Alert> : null}
                  {userLookup ? (
                    <SpaceBetween size="xs" direction="horizontal">
                      <Box>Tokens today: {userLookup.detail.tokensToday.toLocaleString()}</Box>
                      <Box>Cost today: {formatUsd(userLookup.detail.costUsdToday)}</Box>
                      <Button
                        variant="inline-link"
                        onClick={() => setTrendTarget({ kind: 'user', id: userLookup.email, label: userLookup.email })}
                      >
                        View trend
                      </Button>
                    </SpaceBetween>
                  ) : null}
                </SpaceBetween>
              </FormField>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Form>

      <Modal
        visible={trendTarget !== null}
        onDismiss={() => {
          setTrendTarget(null);
          setTrendData(null);
          setTrendError(null);
        }}
        header={trendTarget ? `30-day usage — ${trendTarget.label}` : 'Usage'}
        size="large"
      >
        {trendLoading ? (
          <Spinner />
        ) : trendError ? (
          <Alert type="error">Could not load trend: {trendError}</Alert>
        ) : trendData ? (
          <UsageTrendChart detail={trendData} metric={trendMetric} onMetricChange={setTrendMetric} />
        ) : null}
      </Modal>
    </PageLayout>
  );
}
